import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { Card } from "../../components/UI";
import { WizardButtons } from "./FormControls";
import { WizardStepper } from "./WizardStepper";
import { DateField } from "./DateField";
import { EMPTY_REFERRAL } from "./constants";
import {
  DemographicsSection, InsuranceSection, ReferralSourceSection, PresentingProblemSection,
  DiagnosisSection, MedicationSection, HousingSection, RequestedServicesSection, AdditionalInfoSection,
} from "./Sections";
import { toast } from "sonner";

const TABS = [
  { key: "demographics", label: "Demographics" },
  { key: "insurance", label: "Insurance" },
  { key: "referral_source", label: "Referral Source" },
  { key: "presenting_problem", label: "Presenting Problem" },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "medication", label: "Medication" },
  { key: "housing", label: "Housing/Placement" },
  { key: "requested_services", label: "Requested Services" },
  { key: "additional_information", label: "Additional Information" },
];

export default function ReferralWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_REFERRAL);
  const [tab, setTab] = useState(0);
  const [visited, setVisited] = useState(() => new Set([0]));
  useEffect(() => { setVisited((v) => new Set(v).add(tab)); }, [tab]);
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    api.get("/insurance-companies").then((r) => setCompanies(r.data));
    if (id) {
      api.get(`/referral-forms/${id}`).then((r) => {
        const merged = { ...EMPTY_REFERRAL, ...r.data };
        Object.keys(EMPTY_REFERRAL).forEach((k) => {
          if (typeof EMPTY_REFERRAL[k] === "object" && !Array.isArray(EMPTY_REFERRAL[k])) {
            merged[k] = { ...EMPTY_REFERRAL[k], ...(r.data[k] || {}) };
          }
        });
        setForm(merged);
        setLoading(false);
      }).catch(() => { toast.error("Referral not found"); navigate("/engagement/referrals"); });
    }
  }, [id]);

  const setSection = (key) => (patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const save = async () => {
    if (!form.demographics.first_name || !form.demographics.last_name) {
      toast.error("First and last name are required (Demographics tab)");
      setTab(0);
      return;
    }
    setBusy(true);
    try {
      if (id) await api.put(`/referral-forms/${id}`, form);
      else await api.post("/referral-forms", form);
      toast.success(id ? "Referral updated" : "Referral saved");
      navigate("/engagement/referrals");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const isLast = tab === TABS.length - 1;
  const next = () => (isLast ? save() : setTab(tab + 1));
  const back = () => (tab === 0 ? navigate("/engagement/referrals") : setTab(tab - 1));

  if (loading) return <div className="py-20 text-center text-[#84675B]">Loading referral…</div>;

  const key = TABS[tab].key;

  return (
    <div>
      <div className="mb-4 flex items-baseline gap-2">
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">Referral Form</h1>
        <span className="text-sm text-[#84675B]">/ {id ? "Edit Referral" : "Add New Referral"}</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <WizardStepper tabs={TABS} active={tab} visited={visited} onSelect={setTab} testPrefix="referral-tab" />
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-[#3E2723]">Date Referral Received:</span>
          <DateField testId="date-referral-received" className="w-52" value={form.date_referral_received}
            onChange={(v) => setForm({ ...form, date_referral_received: v })} />
        </div>
      </div>

      <Card className="p-6 lg:p-8" data-testid={`referral-section-${key}`}>
        {key === "demographics" && <DemographicsSection data={form.demographics} set={setSection("demographics")} />}
        {key === "insurance" && <InsuranceSection data={form.insurance} set={setSection("insurance")} companies={companies} />}
        {key === "referral_source" && <ReferralSourceSection data={form.referral_source} set={setSection("referral_source")} />}
        {key === "presenting_problem" && <PresentingProblemSection data={form.presenting_problem} set={setSection("presenting_problem")} />}
        {key === "diagnosis" && <DiagnosisSection data={form.diagnosis} set={setSection("diagnosis")} />}
        {key === "medication" && <MedicationSection data={form.medication} set={setSection("medication")} />}
        {key === "housing" && <HousingSection data={form.housing} set={setSection("housing")} />}
        {key === "requested_services" && <RequestedServicesSection data={form.requested_services} set={setSection("requested_services")} />}
        {key === "additional_information" && <AdditionalInfoSection data={form.additional_information} set={setSection("additional_information")} />}
        <WizardButtons onBack={back} onNext={next} nextLabel={isLast ? "Save & Continue" : "Next"} busy={busy} />
      </Card>
    </div>
  );
}
